import "@fontsource-variable/instrument-sans/wght.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import { StrictMode, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, Show, SignInButton, useAuth, useClerk } from "@clerk/react";
import { BedrockApp } from "@bedrock/app-shell";
import { Icon, StrataMark } from "@bedrock/ui";
import { workingSessionSchema, accountStorageUsageSchema, authCheckResponseSchema, clipEnqueueRequestSchema, deriveAccountStorageKey, multipartAssetCreateRequestSchema, multipartAssetCreateResponseSchema, multipartPartResponseSchema, syncCheckpointSchema, syncV2PullResponseSchema, syncV2PushResponseSchema, syncV2SnapshotSchema, type WorkingSession, type AccountStorageUsage, type SyncOperation } from "@bedrock/domain";
import { MultipartUploadClient, SyncEngine, SyncInvalidationClient, SyncRequestError, syncV2PullPath, type MultipartTransport, type SyncApi } from "@bedrock/local-store";
import { WebLocalStore } from "@bedrock/local-store-web";
import { shouldEnableCloudSync } from "./cloud-sync";
import "./web.css";

const clerkKey = import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"] as string | undefined;
const apiOrigin = (import.meta.env["VITE_API_ORIGIN"] as string | undefined) || window.location.origin;
const cloudSyncEnabled = shouldEnableCloudSync({
  development: import.meta.env.DEV,
  origin: window.location.origin,
  allowedOrigins: import.meta.env.VITE_CLOUD_SYNC_ORIGINS
});
const demoLocalStore = new WebLocalStore();
type TokenProvider = (refresh?: boolean) => Promise<string | null>;
type AccountProfile = { userId: string; kind: "legacy" | "account"; databaseName: string };
const syncCapabilities = ["spreadsheet-v1", "locked-notes-v1", "locked-folders-v1"] as const;
const syncCapabilityQuery = `capabilities=${encodeURIComponent(syncCapabilities.join(","))}`;

async function requestError(response: Response, label: string): Promise<SyncRequestError> {
  let payload: { error?: { code?: unknown; message?: unknown; correlationId?: unknown } } = {};
  try { payload = await response.clone().json() as typeof payload; } catch { /* use the status fallback */ }
  const retryAfter = response.headers.get("retry-after");
  const seconds = retryAfter === null ? Number.NaN : Number(retryAfter);
  const retryDate = retryAfter === null ? Number.NaN : Date.parse(retryAfter);
  return new SyncRequestError({
    retryable: response.status === 408 || response.status === 429 || response.status >= 500,
    status: response.status,
    code: typeof payload.error?.code === "string" ? payload.error.code : `http_${response.status}`,
    message: typeof payload.error?.message === "string" ? payload.error.message : `${label} failed (${response.status})`,
    correlationId: typeof payload.error?.correlationId === "string" ? payload.error.correlationId : response.headers.get("x-correlation-id"),
    retryAt: Number.isFinite(seconds) ? Date.now() + seconds * 1000 : Number.isFinite(retryDate) ? retryDate : null
  });
}

async function authenticatedFetch(tokenProvider: TokenProvider, path: string, init?: RequestInit): Promise<Response> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const token = await tokenProvider(attempt === 1);
    if (!token) throw new SyncRequestError({ retryable: false, status: 401, code: "sign_in_required", message: "Sign in again to resume cloud sync", correlationId: null, retryAt: null });
    let response: Response;
    try {
      response = await fetch(`${apiOrigin}${path}`, { ...init, headers: { authorization: `Bearer ${token}`, ...init?.headers } });
    } catch {
      throw new SyncRequestError({ retryable: true, status: null, code: "network_error", message: "The cloud is temporarily unreachable", correlationId: null, retryAt: null });
    }
    if (response.status === 401 && attempt === 0) continue;
    if (!response.ok) throw await requestError(response, "Cloud request");
    return response;
  }
  throw new SyncRequestError({ retryable: false, status: 401, code: "sign_in_required", message: "Sign in again to resume cloud sync", correlationId: null, retryAt: null });
}

function parseCloudResponse<T>(schema: { parse(value: unknown): T }, value: unknown): T {
  try { return schema.parse(value); }
  catch { throw new SyncRequestError({ retryable: false, status: null, code: "protocol_incompatible", message: "The cloud returned an incompatible sync response", correlationId: null, retryAt: null }); }
}

async function parseCloudJson<T>(response: Response, schema: { parse(value: unknown): T }): Promise<T> {
  try { return parseCloudResponse(schema, await response.json()); }
  catch (error) {
    if (error instanceof SyncRequestError) throw error;
    throw new SyncRequestError({ retryable: false, status: response.status, code: "protocol_incompatible", message: "The cloud returned an incompatible response", correlationId: response.headers.get("x-correlation-id"), retryAt: null });
  }
}

class WorkerSyncApi implements SyncApi {
  readonly #token: TokenProvider;
  constructor(token: TokenProvider) { this.#token = token; }

  async #request(path: string, init?: RequestInit): Promise<unknown> {
    const response = await authenticatedFetch(this.#token, path, { ...init, headers: { "content-type": "application/json", ...init?.headers } });
    try { return await response.json(); }
    catch { throw new SyncRequestError({ retryable: false, status: response.status, code: "protocol_incompatible", message: "The cloud returned an incompatible sync response", correlationId: response.headers.get("x-correlation-id"), retryAt: null }); }
  }

  async push(deviceId: string, cursor: number, operations: SyncOperation[]) {
    return parseCloudResponse(syncV2PushResponseSchema, await this.#request("/api/sync/v2/push", { method: "POST", body: JSON.stringify({ deviceId, cursor, operations, capabilities: syncCapabilities }) }));
  }

  async bootstrap(deviceId: string) {
    return parseCloudResponse(syncV2SnapshotSchema, await this.#request(`/api/sync/v2/bootstrap?deviceId=${encodeURIComponent(deviceId)}&${syncCapabilityQuery}`));
  }

  async pull(deviceId: string, after: number) {
    return parseCloudResponse(syncV2PullResponseSchema, await this.#request(`${syncV2PullPath(deviceId, after)}&${syncCapabilityQuery}`));
  }

  async checkpoint() { return parseCloudResponse(syncCheckpointSchema, await this.#request(`/api/sync/v2/checkpoint?${syncCapabilityQuery}`)); }
}

class WorkerMultipartTransport implements MultipartTransport {
  readonly #token: TokenProvider;
  constructor(token: TokenProvider) { this.#token = token; }
  async #request(path: string, init: RequestInit): Promise<Response> {
    return authenticatedFetch(this.#token, path, init);
  }
  async create(input: { assetId: string; filename: string; mimeType: string; byteLength: number }) {
    const parsed = multipartAssetCreateRequestSchema.parse(input);
    const response = await this.#request("/api/assets/multipart", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(parsed) });
    return parseCloudJson(response, multipartAssetCreateResponseSchema);
  }
  async uploadPart(uploadId: string, partNumber: number, body: Blob) {
    const response = await this.#request(`/api/assets/multipart/${encodeURIComponent(uploadId)}/${partNumber}`, { method: "PUT", headers: { "content-type": "application/octet-stream" }, body });
    return parseCloudJson(response, multipartPartResponseSchema);
  }
  async complete(uploadId: string, parts: Array<{ partNumber: number; etag: string }>) {
    await this.#request(`/api/assets/multipart/${encodeURIComponent(uploadId)}/complete`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ parts }) });
  }
}

function AuthenticatedBedrock() {
  const { getToken, userId, sessionId } = useAuth();
  const clerk = useClerk();
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [resolutionError, setResolutionError] = useState("");
  const tokenProvider = useMemo<TokenProvider>(() => (refresh = false) => getToken(refresh ? { skipCache: true } : undefined), [getToken]);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    const cached = readCachedProfile(userId);
    if (cached) void createAccountProfile(userId, cached).then((value) => { if (active) setProfile(value); });
    void authenticatedFetch(tokenProvider, "/api/auth/check").then(async (response) => {
      const checked = await parseCloudJson(response, authCheckResponseSchema);
      if (checked.userId !== userId) throw new SyncRequestError({ retryable: false, status: 409, code: "account_identity_mismatch", message: "The verified cloud account does not match this Clerk session", correlationId: null, retryAt: null });
      const kind = checked.legacyLocalProfile || cached === "legacy" ? "legacy" as const : "account" as const;
      localStorage.setItem(profileCacheKey(userId), JSON.stringify({ userId, kind }));
      const resolved = await createAccountProfile(userId, kind);
      if (active) { setProfile(resolved); setResolutionError(""); }
    }).catch((error: unknown) => {
      const canUseCached = cached && error instanceof SyncRequestError && error.detail.retryable;
      if (!canUseCached && active) {
        setProfile(null);
        setResolutionError(error instanceof Error ? error.message : "Your private account could not be verified");
      }
    });
    return () => { active = false; };
  }, [tokenProvider, userId]);

  if (!userId) return <AccountResolutionScreen message="Clerk did not provide a personal account ID."/>;
  if (!profile || profile.userId !== userId) return <AccountResolutionScreen message={resolutionError || "Opening your private account…"}/>;
  return <AccountBedrock key={profile.userId} profile={profile} tokenProvider={tokenProvider} getToken={getToken} onSignOut={async () => {
    if (!sessionId) throw new Error("The active Clerk session could not be identified");
    await clerk.signOut({ sessionId });
  }}/>;
}

function AccountBedrock({ profile, tokenProvider, getToken, onSignOut }: { profile: AccountProfile; tokenProvider: TokenProvider; getToken: ReturnType<typeof useAuth>["getToken"]; onSignOut: () => Promise<void> }) {
  const localStore = useMemo(() => new WebLocalStore(profile.databaseName), [profile.databaseName]);
  const closeTimer = useRef<number | undefined>(undefined);
  const api = useMemo(() => new WorkerSyncApi(tokenProvider), [tokenProvider]);
  const uploader = useMemo(() => new MultipartUploadClient(new WorkerMultipartTransport(tokenProvider)), [tokenProvider]);
  const enqueueClip = useMemo(() => async (input: { clipId: string; itemId: string; url: string }) => {
    const parsed = clipEnqueueRequestSchema.parse(input);
    await authenticatedFetch(tokenProvider, "/api/clips", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(parsed)
    });
  }, [tokenProvider]);
  const downloadAsset = useMemo(() => async (assetId: string) => {
    const response = await authenticatedFetch(tokenProvider, `/api/assets/${encodeURIComponent(assetId)}`);
    return response.blob();
  }, [tokenProvider]);
  const getAccountStorageUsage = useMemo(() => async (): Promise<AccountStorageUsage> => {
    const response = await authenticatedFetch(tokenProvider, "/api/account/storage");
    return parseCloudJson(response, accountStorageUsageSchema);
  }, [tokenProvider]);
  const workingSessionCloud = useMemo(() => ({
    get: async () => parseCloudJson(await authenticatedFetch(tokenProvider, "/api/account/working-session"), workingSessionSchema.nullable()),
    save: async (session: WorkingSession) => parseCloudJson(await authenticatedFetch(tokenProvider, "/api/account/working-session", {
      method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(session)
    }), workingSessionSchema)
  }), [tokenProvider]);
  useEffect(() => {
    if (closeTimer.current !== undefined) window.clearTimeout(closeTimer.current);
    return () => { closeTimer.current = window.setTimeout(() => void localStore.close(), 0); };
  }, [localStore]);
  useEffect(() => {
    const deviceId = localStorage.getItem("bedrock-device-id") ?? crypto.randomUUID();
    localStorage.setItem("bedrock-device-id", deviceId);
    const sync = new SyncEngine(localStore, api, deviceId, (state) => {
      window.dispatchEvent(new CustomEvent("bedrock:sync-state", { detail: state }));
    }, (clock) => window.dispatchEvent(new CustomEvent("bedrock:remote-clock", { detail: clock })));
    const invalidations = new SyncInvalidationClient(async () => {
      const token = await getToken();
      if (!token) throw new Error("No active Bedrock session");
      const url = new URL("/api/sync/socket", apiOrigin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.searchParams.set("deviceId", deviceId);
      url.searchParams.set("after", String(await localStore.getSyncCursor() ?? 0));
      url.searchParams.set("token", token);
      return url.toString();
    }, () => { void sync.syncNow(); });
    const onOnline = () => void sync.syncNow();
    const onFocus = () => void sync.syncNow();
    const onVisibility = () => { if (document.visibilityState === "visible") void sync.syncNow(); };
    const unsubscribe = localStore.subscribe(() => sync.schedule());
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    invalidations.start();
    sync.start();
    return () => { sync.dispose(); invalidations.stop(); unsubscribe(); window.removeEventListener("online", onOnline); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVisibility); };
  }, [api, getToken, localStore]);
  return <BedrockApp key={profile.userId} sessionScope={profile.userId} workingSessionCloud={workingSessionCloud} store={localStore} platform="web" authMode="authenticated" seedDemo={false} enqueueClip={enqueueClip} uploadAsset={(input) => uploader.upload(input)} downloadAsset={downloadAsset} getAccountStorageUsage={getAccountStorageUsage} onSignOut={onSignOut}/>;
}

function SignInPreview() {
  return <aside className="sign-in-preview" aria-hidden="true">
    <header className="sign-in-preview__header"><span>BEDROCK / SPACE 01</span><span>YOUR PRIVATE CANVAS</span></header>
    <div className="sign-in-preview__canvas">
      <span className="sign-in-preview__coordinate sign-in-preview__coordinate--x">X 148</span>
      <span className="sign-in-preview__coordinate sign-in-preview__coordinate--y">Y 032</span>
      <article className="sign-in-artifact sign-in-artifact--note">
        <small>THOUGHT / 14:32</small>
        <strong>A quiet place for ideas in progress.</strong>
        <i/><i/><i/>
      </article>
      <article className="sign-in-artifact sign-in-artifact--document">
        <small>FIELD NOTES</small>
        <strong>Keep the useful bits close.</strong>
        <p>Notes, references, and half-formed thoughts—all arranged your way.</p>
        <span>01</span>
      </article>
      <article className="sign-in-artifact sign-in-artifact--event">
        <small>NEXT / THU 18</small>
        <strong>Review research</strong>
        <span>09:30</span>
      </article>
      <article className="sign-in-artifact sign-in-artifact--folder">
        <div className="sign-in-artifact__folder-back"/>
        <div className="sign-in-artifact__folder-front"><small>REFERENCE</small><strong>12 items</strong></div>
      </article>
    </div>
    <footer className="sign-in-preview__footer"><span><i/>LOCAL FIRST</span><span>SYNCED WHEN ONLINE</span></footer>
  </aside>;
}

function SignInLayout({ children, resolving = false }: { children: ReactNode; resolving?: boolean }) {
  return <main className={`sign-in-screen ${resolving ? "is-resolving" : ""}`}>
    <div className="sign-in-shell">
      <section className="sign-in-panel" aria-labelledby="sign-in-title">
        <header className="sign-in-brand">
          <StrataMark size={38}/>
          <div><strong>Bedrock</strong><span>Private spatial notes</span></div>
        </header>
        <div className="sign-in-copy">{children}</div>
        <footer className="sign-in-panel__footer"><span><i/>Private vault</span><span>Local-first by design</span></footer>
      </section>
      <SignInPreview/>
    </div>
  </main>;
}

function AccountResolutionScreen({ message }: { message: string }) {
  return <SignInLayout resolving>
    <p className="sign-in-kicker">PRIVATE ACCOUNT</p>
    <h1 id="sign-in-title">Preparing your vault.</h1>
    <p className="sign-in-description" aria-live="polite">{message}</p>
    <div className="sign-in-progress" aria-hidden="true"><span/></div>
  </SignInLayout>;
}

function profileCacheKey(userId: string): string { return `bedrock-verified-profile-v1:${userId}`; }

function readCachedProfile(userId: string): AccountProfile["kind"] | null {
  try {
    const value = JSON.parse(localStorage.getItem(profileCacheKey(userId)) ?? "null") as { userId?: unknown; kind?: unknown } | null;
    return value?.userId === userId && (value.kind === "legacy" || value.kind === "account") ? value.kind : null;
  } catch { return null; }
}

async function createAccountProfile(userId: string, kind: AccountProfile["kind"]): Promise<AccountProfile> {
  return { userId, kind, databaseName: kind === "legacy" ? "bedrock-local" : `bedrock-local-${await deriveAccountStorageKey(userId)}` };
}

function SignedOutScreen() {
  return <SignInLayout>
    <p className="sign-in-kicker">PRIVATE SPATIAL NOTES</p>
    <h1 id="sign-in-title">Make room for what matters.</h1>
    <p className="sign-in-description">Bedrock gives notes, references, and half-formed ideas a private place to stay useful—offline first, and in sync when you reconnect.</p>
    <SignInButton mode="modal"><button type="button" className="sign-in-action"><span>Continue with Google</span><Icon name="arrow-right"/></button></SignInButton>
    <p className="sign-in-assurance">Your approved account opens its own private library.</p>
  </SignInLayout>;
}

function Root() {
  if (!clerkKey || clerkKey.includes("replace_me")) return <BedrockApp store={demoLocalStore} platform="web" authMode="local"/>;
  if (!cloudSyncEnabled) return <BedrockApp store={demoLocalStore} platform="web" authMode="local" seedDemo={false}/>;
  return <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
    <Show when="signed-in" fallback={<SignedOutScreen/>}><AuthenticatedBedrock/></Show>
  </ClerkProvider>;
}

const root = document.getElementById("root");
if (!root) throw new Error("Bedrock root element was not found");
createRoot(root).render(<StrictMode><Root/></StrictMode>);

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => void navigator.serviceWorker.register("/sw.js"));
}
